import React, { useEffect, useState } from 'react';

import AsyncImage from '@components/asyncImage/AsyncImage';
import ModalBoolean from '@components/modals/ModalBoolean';
import DemmiIconButton, { IconButtonSize } from '@demmi-ui/IconButton';
import { GalleryEditorHelper } from '@helpers/gelleryEditor.helper';
import { DemmiFS } from '@subhanhabib/demmilib';

interface Props
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  CSSBlock: string;
  image?: DemmiFS.DemmiImageResource;
  index: number;
  isOverlay?: boolean;
  isVendor?: boolean;
  onClickDelete?: (index: number) => void;
}

const GalleryEditorImage = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      CSSBlock,
      image,
      index,
      isOverlay,
      isVendor,
      onClickDelete,
      ...props
    }: Props,
    ref,
  ) => {
    if (!image) return null;
    const [scale, setScale] = useState(1);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    // Only for the overlay when rearranging
    useEffect(() => {
      const el = wrapperRef.current?.parentElement;
      if (!el || !isOverlay) return;

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'style'
          ) {
            setScale(GalleryEditorHelper.getScaleFromElement(el));
          }
        });
      });

      observer.observe(el, { attributes: true, attributeFilter: ['style'] });
      return () => observer.disconnect();
    }, []);

    // Only for non-overlay images
    useEffect(() => {
      if (!isOverlay) {
        if (props.style) setScale(GalleryEditorHelper.getScale(props.style));
        else setScale(1);
      }
    }, [props]);

    const dialogDelete = (onClickDelete: (index: number) => void) => (
      <span className={`${CSSBlock}__button-delete`}>
        <ModalBoolean
          titleText={`Are you sure you want to delete this image?`}
          descriptionText="Warning: This action cannot be undone!"
          customView={
            <span className={`${CSSBlock}-modal-delete__thumbnail`}>
              <AsyncImage
                src={image.thumbnail}
                localLoadingIndicator
                isVendor={isVendor}
              />
            </span>
          }
          triggerEl={
            <DemmiIconButton
              faIcon="fa-solid fa-trash"
              overlay
              size={IconButtonSize.SMALL}
            />
          }
          onConfirm={() => onClickDelete(index)}
        />
      </span>
    );

    return (
      <span
        className={`${CSSBlock}__thumbnail-wrapper`}
        ref={wrapperRef}
        style={GalleryEditorHelper.getThumbnailWrapperCSS(index === 0)}>
        <div
          className={`${CSSBlock}__thumbnail  ${isOverlay ? `${CSSBlock}__thumbnail--overlay` : ''}`}
          ref={ref}
          key={image.thumbnail}
          {...props}
          style={{
            ...props.style,
            ...GalleryEditorHelper.getThumbnailCSS(scale),
          }}>
          <AsyncImage
            src={image.thumbnail}
            localLoadingIndicator
            isVendor={isVendor}
          />
        </div>
        {onClickDelete &&
          !isOverlay &&
          !props.style?.transform &&
          dialogDelete(onClickDelete)}
      </span>
    );
  },
);

GalleryEditorImage.displayName = 'GalleryEditorImage';
export default GalleryEditorImage;
